<script setup lang="ts">
import type { PreparedBarometerAuction } from '@autobid/ui/types/components/AuctionsData'
import StartDateCalendarPopper from '@autobid/ui/componentsGlobal/dynamic/auctionList/shared/StartDateCalendarPopper.vue'
import { formatStartDate } from '@autobid/ui/utils/auctions/formatStartDate'
import { formatDate } from '@autobid/ui/utils/date/formatDate'
import { getDayName } from '@autobid/ui/utils/date/getDayName'

interface Props {
  auction: PreparedBarometerAuction
}

const props = defineProps<Props>()

function parseTitle(title: string) {
  if (title.includes(' - ')) {
    const [head, tail] = title.split(' - ')
    return {
      head: `${head} - `,
      tail,
    }
  }
  return {
    head: '',
    tail: title,
  }
}

const title = computed(() => {
  return parseTitle(props.auction.title ?? '')
})

const { locale } = useI18n()
</script>

<template>
  <div class="mb-4 hidden lg:block">
    <div
      class="relative z-20 mx-auto flex h-max w-full flex-col gap-4 overflow-visible !bg-white bg-primary px-2 py-3.5 text-white lg:flex-row lg:items-center lg:justify-between lg:bg-transparent lg:px-4 lg:text-primary"
    >
      <div class="container mx-auto text-primary">
        <div class="flex h-full gap-2 bg-white">
          <div class="flex flex-col px-4 text-4xl">
            <span> {{ title.head }}</span>
            <span>
              {{ title.tail }}
            </span>
          </div>

          <div
            class="flex max-w-xl flex-col justify-end border-l border-primary px-4 xl:max-w-2xl"
          >
            <span v-for="item in auction.descriptions" :key="item">{{
              item
            }}</span>
          </div>
          <DynamicAuctionListSharedCatalogPopper
            v-if="auction"
            class="ml-auto mt-auto h-10"
            :app-id="auction.appId"
            :catalog="auction.catalog"
            :restrictions="auction.restrictions"
            :stage="auction.stage"
            :type="auction.type"
          >
            <ElementsButton
              class="ml-auto mt-auto h-10 border border-primary px-14 text-primary"
              background-color="white"
            >
              {{ $t('auction-list.catalog') }}
            </ElementsButton>
          </DynamicAuctionListSharedCatalogPopper>
        </div>
      </div>
    </div>
    <div
      class="full-width-component relative bg-primary py-1 text-base text-white"
    >
      <div
        class="container mx-auto flex items-center divide-x divide-white px-4"
      >
        <div class="px-4 font-normal">
          {{ $t('auction-list.auction-number') }} {{ auction.number }}
        </div>
        <div class="px-4 font-bold">
          <StartDateCalendarPopper
            :title="auction.title"
            :type="auction.type"
            :department="auction.department"
            :start-date="auction.startDate"
            :app-id="auction.appId"
            :stage="auction.stage"
            :slug="auction.slug"
            :descriptions="auction.descriptions"
          >
            {{
              getDayName(auction.startDate, {
                weekday: 'long',
                lang: locale,
              })
            }}
            {{ formatDate(auction.startDate) }}
          </StartDateCalendarPopper>
        </div>
        <div class="px-4 font-bold">
          <StartDateCalendarPopper
            :title="auction.title"
            :type="auction.type"
            :department="auction.department"
            :start-date="auction.startDate"
            :app-id="auction.appId"
            :stage="auction.stage"
            :slug="auction.slug"
            :descriptions="auction.descriptions"
          >
            <span class="hidden lg:inline-block">{{ $t('start') }}</span>
            {{
              formatStartDate(auction.startDate, {
                showFullDate: false,
                $t,
              })
            }}
          </StartDateCalendarPopper>
        </div>
      </div>
    </div>
  </div>
</template>
